import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const navLinks = [
  { label: 'Work', id: 'section-work' },
  { label: 'Assess', id: 'section-assess' },
  { label: 'Experience', id: 'section-experience' },
  { label: 'Awards', id: 'section-awards' },
  // { label: 'Notes', id: 'section-notes' }, // hidden while in draft
];

/**
 * MainNavbar
 * 
 * Appears after hero scroll. Fixed, full-width, stone-900 with subtle backdrop blur.
 * Left: "Ivan" serif italic brand mark.
 * Right: 5 section links in mono uppercase.
 * Hover: yellow accent underline slides in from left.
 * Entrance: nav slides down; links stagger in from opacity 0.
 * Scroll-to: finds the section by ID within the provided scrollContainer ref.
 */
export default function MainNavbar({ scrollContainerRef }) {
  const [activeId, setActiveId] = useState(null);

  // Track which section is active via IntersectionObserver on the scroll container
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const sectionEls = navLinks
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { root: container, threshold: 0.4 }
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  const scrollToSection = (id) => {
    const container = scrollContainerRef?.current;
    const el = document.getElementById(id);
    if (!el || !container) return;
    container.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
  };

  const linkVariants = {
    hidden: { opacity: 0, y: -8 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.15 + i * 0.07,
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  const brandVariants = {
    hidden: { opacity: 0, x: -12 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <motion.nav
      initial={{ y: -72, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -72, opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-4 sm:px-6 md:px-12 justify-between bg-stone-900/95 backdrop-blur-md border-b border-stone-800/60"
    >
      {/* Brand mark */}
      <motion.button
        variants={brandVariants}
        initial="hidden"
        animate="visible"
        onClick={() => {
          const container = scrollContainerRef?.current;
          if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className="font-serif text-[19px] min-[375px]:text-[20px] sm:text-[22px] font-bold italic tracking-tight text-stone-50 hover:text-yellow-500 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-900 flex-shrink-0"
        aria-label="Scroll to top"
      >
        Ivan
      </motion.button>

      {/* Navigation links */}
      <div className="flex items-center gap-2.5 min-[375px]:gap-3.5 sm:gap-6 md:gap-9" role="navigation" aria-label="Page sections">
        {navLinks.map((link, i) => {
          const isActive = activeId === link.id;
          return (
            <motion.button
              key={link.id}
              custom={i}
              variants={linkVariants}
              initial="hidden"
              animate="visible"
              onClick={() => scrollToSection(link.id)}
              className="relative group text-[8.5px] min-[375px]:text-[9px] md:text-[10px] font-mono uppercase tracking-[0.12em] min-[375px]:tracking-[0.15em] sm:tracking-[0.18em] md:tracking-[0.22em] transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-900 rounded-sm flex-shrink-0"
              style={{ color: isActive ? '#f5f5f4' : '#a8a29e' }}
            >
              {link.label}

              {/* Yellow accent underline — slides in from left on hover or active */}
              <span
                className="absolute -bottom-[3px] left-0 h-[1.5px] bg-yellow-500 transition-all duration-350 ease-out"
                style={{
                  width: isActive ? '100%' : '0%',
                  opacity: isActive ? 0.9 : 0,
                }}
              />
              <span
                className="absolute -bottom-[3px] left-0 h-[1.5px] bg-yellow-500 opacity-0 group-hover:opacity-70 group-hover:w-full w-0 transition-all duration-300 ease-out"
              />
            </motion.button>
          );
        })}

        {/* Divider + Contact */}
        <motion.span
          custom={navLinks.length}
          variants={linkVariants}
          initial="hidden"
          animate="visible"
          className="hidden md:block h-3 w-px bg-stone-700"
          aria-hidden="true"
        />
        <motion.a
          custom={navLinks.length + 1}
          variants={linkVariants}
          initial="hidden"
          animate="visible"
          href="mailto:ivansridana@gmail.com"
          className="hidden md:inline text-[9px] md:text-[10px] font-mono uppercase tracking-[0.22em] text-stone-400 hover:text-stone-50 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-900 rounded-sm"
        >
          Contact
        </motion.a>
      </div>
    </motion.nav>
  );
}
